"use client"

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  getWorkspaceNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type UserNotificationV3,
} from "@/lib/api-v3"
import { getTokens } from "@/lib/auth"
import { useUser } from "@/components/user-context"
import { getLastOpened, setLastOpened, type LastOpenedEntityType } from "@/lib/last-opened"

// ─── Константы ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20
/** Интервал фонового обновления, мс */
const POLL_INTERVAL = 60_000

// ─── Типы ─────────────────────────────────────────────────────────────────────

interface NotificationsContextType {
  /** Все загруженные уведомления (новые → старые) */
  notifications: UserNotificationV3[]
  /** Есть ли хотя бы одно непрочитанное */
  hasUnread: boolean
  isLoading: boolean
  /** Загружены ли все страницы */
  isFullyLoaded: boolean
  /** Загрузить следующую страницу */
  loadMore: () => Promise<void>
  isLoadingMore: boolean
  /** Отметить уведомление прочитанным (API + локальный стейт) */
  markAsRead: (notificationId: number) => Promise<void>
  /** Отметить все уведомления прочитанными (API + локальный стейт) */
  markAllAsRead: () => Promise<void>
  /** Перезагрузить с начала */
  refresh: () => Promise<void>
  /** Растёт после каждой успешной загрузки первой страницы (начальной, поллинга, возврата из фона) */
  syncVersion: number
  /** ID инцидентов, у которых есть непрочитанные уведомления новее последнего открытия */
  unreadIncidentIds: Set<number>
  /** ID назначений, у которых есть непрочитанные уведомления новее последнего открытия */
  unreadAssignmentIds: Set<number>
  /** Записать время открытия сущности, пересчитать точки */
  recordOpened: (type: LastOpenedEntityType, id: number) => void
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

// ─── Провайдер ────────────────────────────────────────────────────────────────

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { userWorkspace } = useUser()
  const workspaceId = userWorkspace?.workspaceId

  const [notifications, setNotifications] = useState<UserNotificationV3[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isFullyLoaded, setIsFullyLoaded] = useState(false)
  const [syncVersion, setSyncVersion] = useState(0)

  // Отслеживаем сколько реально загружено для пагинации
  const loadedCountRef = useRef(0)

  const hasUnread = notifications.some((n) => !n.isRead)

  // Счётчик, который инкрементируется при вызове recordOpened —
  // это заставляет useMemo пересчитать наборы ID точек.
  const [lastOpenedVersion, setLastOpenedVersion] = useState(0)

  const unreadIncidentIds = useMemo(() => {
    const ids = new Set<number>()
    for (const n of notifications) {
      if (n.isRead) continue
      try {
        const raw = n.notification?.data
        if (!raw) continue
        const d: Record<string, unknown> = typeof raw === "string" ? JSON.parse(raw) : (raw as Record<string, unknown>)
        if (typeof d.incidentId !== "number") continue
        const incidentId = d.incidentId
        const notifCreated = new Date(n.notification?.created ?? n.created).getTime()
        if (notifCreated > getLastOpened("incident", incidentId)) {
          ids.add(incidentId)
        }
      } catch { /* ignore */ }
    }
    return ids
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, lastOpenedVersion])

  const unreadAssignmentIds = useMemo(() => {
    const ids = new Set<number>()
    for (const n of notifications) {
      if (n.isRead) continue
      try {
        const raw = n.notification?.data
        if (!raw) continue
        const d: Record<string, unknown> = typeof raw === "string" ? JSON.parse(raw) : (raw as Record<string, unknown>)
        if (typeof d.assignmentId !== "number") continue
        const assignmentId = d.assignmentId
        const notifCreated = new Date(n.notification?.created ?? n.created).getTime()
        if (notifCreated > getLastOpened("assignment", assignmentId)) {
          ids.add(assignmentId)
        }
      } catch { /* ignore */ }
    }
    return ids
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, lastOpenedVersion])

  const recordOpened = useCallback((type: LastOpenedEntityType, id: number) => {
    setLastOpened(type, id)
    setLastOpenedVersion((v) => v + 1)
  }, [])

  // ── Загрузить первую страницу ─────────────────────────────────────────────

  const fetchFirst = useCallback(async () => {
    const tokens = getTokens()
    if (!tokens?.accessToken || !workspaceId) return

    setIsLoading(true)
    try {
      const data = await getWorkspaceNotifications(tokens.accessToken, workspaceId, {
        skip: 0,
        take: PAGE_SIZE,
      })
      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.notification?.created ?? b.created).getTime() -
          new Date(a.notification?.created ?? a.created).getTime()
      )
      // Дедупликация на случай дублей от сервера
      const unique = [...new Map(sorted.map((n) => [n.notificationId, n])).values()]
      setNotifications(unique)
      // Пагинацию отслеживаем по числу сырых записей от API,
      // потому что сервер может вернуть N записей для N пользователей
      // с одним и тем же notificationId — после дедупликации их меньше.
      loadedCountRef.current = data.length
      setIsFullyLoaded(data.length < PAGE_SIZE)
      setSyncVersion((v) => v + 1)
    } catch (err) {
      console.error("[notifications] fetchFirst:", err)
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId])

  // ── Загрузить следующую страницу ──────────────────────────────────────────

  const loadMore = useCallback(async () => {
    if (isLoadingMore || isFullyLoaded) return
    const tokens = getTokens()
    if (!tokens?.accessToken || !workspaceId) return

    setIsLoadingMore(true)
    try {
      const data = await getWorkspaceNotifications(tokens.accessToken, workspaceId, {
        skip: loadedCountRef.current,
        take: PAGE_SIZE,
      })
      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.notification?.created ?? b.created).getTime() -
          new Date(a.notification?.created ?? a.created).getTime()
      )
      setNotifications((prev) => {
        // Дедупликация по notificationId
        const ids = new Set(prev.map((n) => n.notificationId))
        const fresh = sorted.filter((n) => !ids.has(n.notificationId))
        return [...prev, ...fresh]
      })
      loadedCountRef.current += sorted.length
      setIsFullyLoaded(sorted.length < PAGE_SIZE)
    } catch (err) {
      console.error("[notifications] loadMore:", err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, isFullyLoaded, workspaceId])

  // ── Отметить прочитанным ──────────────────────────────────────────────────

  const markAsRead = useCallback(async (notificationId: number) => {
    const tokens = getTokens()
    if (!tokens?.accessToken) return
    try {
      await markNotificationAsRead(tokens.accessToken, notificationId)
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      )
    } catch (err) {
      console.error("[notifications] markAsRead:", err)
    }
  }, [])

  // ── Отметить все прочитанными ─────────────────────────────────────────────

  const markAllAsRead = useCallback(async () => {
    const tokens = getTokens()
    if (!tokens?.accessToken || !workspaceId) return
    try {
      await markAllNotificationsAsRead(tokens.accessToken, workspaceId)
      setNotifications((prev) =>
        prev.map((n) => n.isRead ? n : { ...n, isRead: true, readAt: new Date().toISOString() })
      )
    } catch (err) {
      console.error("[notifications] markAllAsRead:", err)
      throw err
    }
  }, [workspaceId])

  // ── Перезагрузить ─────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    loadedCountRef.current = 0
    setIsFullyLoaded(false)
    await fetchFirst()
  }, [fetchFirst])

  // ── Начальная загрузка при появлении workspaceId ─────────────────────────

  useEffect(() => {
    if (workspaceId) {
      loadedCountRef.current = 0
      setIsFullyLoaded(false)
      fetchFirst()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])

  // ── Фоновый поллинг ───────────────────────────────────────────────────────

  const syncFirstPage = useCallback(() => {
    const tokens = getTokens()
    if (!tokens?.accessToken || !workspaceId) return
    // Тихо обновляем первую страницу для актуальности hasUnread
    getWorkspaceNotifications(tokens.accessToken, workspaceId, { skip: 0, take: PAGE_SIZE })
      .then((data) => {
        const sorted = [...data].sort(
          (a, b) =>
            new Date(b.notification?.created ?? b.created).getTime() -
            new Date(a.notification?.created ?? a.created).getTime()
        )
        setNotifications((prev) => {
          // Обновляем isRead существующих + добавляем новые
          const map = new Map(prev.map((n) => [n.notificationId, n]))
          for (const n of sorted) map.set(n.notificationId, n)
          return [...map.values()].sort(
            (a, b) =>
              new Date(b.notification?.created ?? b.created).getTime() -
              new Date(a.notification?.created ?? a.created).getTime()
          )
        })
        setSyncVersion((v) => v + 1)
      })
      .catch(() => {})
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId) return
    const id = setInterval(syncFirstPage, POLL_INTERVAL)
    // Возврат из фона (в т.ч. открытие приложения из push-уведомления) — не ждём следующего тика
    const onVisible = () => {
      if (document.visibilityState === "visible") syncFirstPage()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [workspaceId, syncFirstPage])

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        hasUnread,
        isLoading,
        isFullyLoaded,
        loadMore,
        isLoadingMore,
        markAsRead,
        markAllAsRead,
        refresh,
        syncVersion,
        unreadIncidentIds,
        unreadAssignmentIds,
        recordOpened,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

// ─── Хук ─────────────────────────────────────────────────────────────────────

export function useNotifications() {
  const ctx = useContext(NotificationsContext)
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider")
  return ctx
}
